import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuList, Portal, Text, Grid, GridItem, Divider, Tag } from '@chakra-ui/react';
import { Checkbox } from '../checkbox/checkbox';
import { Button } from '../button/button';
import { ButtonVariant } from '../../core/enums';
import { styles } from './checkbox-menu.styles';
import theme from '../../theme';
import { CheckboxTree } from './checkbox-tree';
import { Conditional } from '@codametrix/ui-components';
import Input from '../../components/input/input';
import { objectUtils } from '@codametrix/ui-common';
const classes = styles();
const CheckboxMenu = (props)=>{
    const { label, options, onApplyFilters, selectedOptions, menuIcon, menuRightIcon, isTree, withTypeAhead } = props;
    const [selected, setSelected] = useState(selectedOptions);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const prevSelectedOptions = useRef([]);
    // Handle search text not supported for tree searching yet.
    const handleSearchText = (ev)=>{
        setSearchText(ev.target.value);
        if (ev.target.value) {
            setFilteredOptions(options.filter((option)=>option?.label?.toLowerCase().includes(ev.target.value?.toLowerCase())));
        } else {
            setFilteredOptions(options);
        }
    };
    // set options by default
    useEffect(()=>{
        setFilteredOptions(options);
    }, [
        options
    ]);
    const handleCheckboxChange = (option)=>{
        if (selected?.some((o)=>o.value === option.value)) {
            setSelected(selected.filter((o)=>o.value !== option.value));
        } else {
            setSelected([
                ...selected,
                option
            ]);
        }
    };
    const getCheckboxItem = (item)=>{
        return {
            label: item.helpText ?? '',
            value: item.key ?? ''
        };
    };
    const handleSelectAllChildren = (selectedOption)=>{
        let tempSelected = JSON.parse(JSON.stringify(selected));
        selectedOption?.children?.forEach((option)=>{
            if (!tempSelected?.some((o)=>o.value === option.key)) {
                tempSelected.push(getCheckboxItem(option));
            }
        });
        if (!(tempSelected.length > 0)) {
            tempSelected.push({
                label: selectedOption.label,
                value: selectedOption.value
            });
        }
        setSelected(tempSelected);
    };
    const handleDeselectAllChildren = (selectedOption)=>{
        let tempSelected = JSON.parse(JSON.stringify(selected));
        tempSelected = tempSelected?.filter((selected)=>{
            const isNotChild = selectedOption?.children?.map(getCheckboxItem)?.find((o)=>o.value === selected.value);
            return isNotChild === undefined;
        })?.filter((el)=>el.value !== selectedOption.value);
        setSelected(tempSelected);
    };
    const handleClear = ()=>{
        setSearchText('');
        setSelected([]);
        setFilteredOptions(options);
    };
    const handleCancelClick = ()=>{
        setIsOpen(false);
    };
    // checks if the options are equal even if the indexes are different
    const isOptionsChanged = ()=>{
        return selected?.length === selectedOptions?.length && selectedOptions?.every((elem)=>selected.some((elem2)=>objectUtils.isEqual(elem, elem2)));
    };
    useEffect(()=>{
        if (JSON.stringify(selectedOptions) !== JSON.stringify(prevSelectedOptions.current)) {
            setSelected(selectedOptions);
            prevSelectedOptions.current = selectedOptions;
        }
    }, [
        selectedOptions
    ]);
    const handleApplyClick = ()=>{
        onApplyFilters(selected);
        setIsOpen(false);
    };
    useEffect(()=>{
        if (!isOpen) {
            setSelected(selectedOptions);
        }
    }, [
        isOpen,
        selectedOptions
    ]);
    return /*#__PURE__*/ _jsxs(Menu, {
        closeOnSelect: false,
        isOpen: isOpen,
        onClose: ()=>setIsOpen(false),
        strategy: "fixed",
        children: [
            /*#__PURE__*/ _jsxs(MenuButton, {
                role: "menubutton",
                onClick: ()=>setIsOpen(true),
                sx: Object.assign({}, classes.menuButton, isOpen ? classes.activeMenuButton : {}),
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        role: "actionbutton",
                        leftIcon: menuIcon,
                        rightIcon: menuRightIcon,
                        variant: ButtonVariant.LINKS,
                        label: selected?.length ? selected[0].label : label,
                        sx: classes.labelButton
                    }),
                    selected?.length > 1 && /*#__PURE__*/ _jsx(Tag, {
                        role: "tag",
                        size: 'xs',
                        variant: "solid",
                        sx: classes.tag,
                        children: /*#__PURE__*/ _jsxs(Text, {
                            sx: classes.tagText,
                            children: [
                                "+",
                                selected?.length - 1
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Portal, {
                children: /*#__PURE__*/ _jsxs(MenuList, {
                    role: "menulist",
                    sx: classes.menuList,
                    children: [
                        /*#__PURE__*/ _jsxs(Grid, {
                            flexDirection: 'column',
                            templateColumns: '1fr 1fr',
                            alignItems: 'center',
                            children: [
                                /*#__PURE__*/ _jsx(GridItem, {
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        sx: classes.menuListHeaderText,
                                        children: label
                                    })
                                }),
                                /*#__PURE__*/ _jsx(GridItem, {
                                    justifySelf: 'flex-end',
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        variant: ButtonVariant.LINKS,
                                        label: "clear",
                                        onClick: handleClear,
                                        sx: classes.clearButton,
                                        _hover: {
                                            background: 'none'
                                        },
                                        _disabled: {
                                            color: theme.colors.text['lowEmphasis']
                                        }
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Conditional, {
                            every: [
                                withTypeAhead === true
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                value: searchText,
                                autoFocus: true,
                                onChange: handleSearchText,
                                name: 'typeAhead',
                                placeholder: 'Search for...',
                                type: 'text',
                                className: 'form-input'
                            })
                        }),
                        /*#__PURE__*/ _jsx(Divider, {
                            sx: Object.assign({}, classes.dividerColor, classes.topDivider)
                        }),
                        isTree && options.map((option, idx)=>/*#__PURE__*/ _jsx(CheckboxTree, {
                                options: option,
                                handleCheckboxChange: handleCheckboxChange,
                                selected: selected,
                                handleSelectAllChildren: handleSelectAllChildren,
                                handleDeselectAllChildren: handleDeselectAllChildren
                            }, idx)),
                        !isTree && filteredOptions.map((option)=>/*#__PURE__*/ _jsx(MenuItem, {
                                sx: Object.assign({}, classes.MenuItem, selected?.find((o)=>o.value === option.value) !== undefined ? classes.selectedMenuItem : {}),
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    dataTestId: `checkbox-${option.value}`,
                                    isChecked: selected?.find((o)=>o.value === option.value) !== undefined,
                                    onChange: ()=>{
                                        handleCheckboxChange(option);
                                    },
                                    label: option.label,
                                    iconColor: theme.colors.opacity.primary,
                                    checkboxColor: selected?.find((o)=>o.value === option.value) !== undefined ? theme.colors.accent[100] : theme.colors.foundation[300],
                                    textColor: theme.colors.text['mediumEmphasis'],
                                    onClick: ()=>handleCheckboxChange(option)
                                }, option.value)
                            })),
                        /*#__PURE__*/ _jsx(Divider, {
                            sx: Object.assign({}, classes.dividerColor, classes.bottomDivider)
                        }),
                        /*#__PURE__*/ _jsxs(Grid, {
                            flexDirection: 'column',
                            templateColumns: 'auto 1fr',
                            children: [
                                /*#__PURE__*/ _jsx(Grid, {
                                    display: "flex",
                                    alignItems: 'center',
                                    justifyContent: "flex-start",
                                    children: isTree && options?.[0]?.children?.length && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                sx: classes.footerText,
                                                color: theme.colors.text['mediumEmphasis'],
                                                children: selected?.length
                                            }),
                                            " ",
                                            /*#__PURE__*/ _jsx(Text, {
                                                sx: classes.footerText,
                                                color: theme.colors.text['lowEmphasis'],
                                                children: `of ${options?.[0]?.children?.length}`
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Grid, {
                                    justifyContent: "end",
                                    display: "flex",
                                    gap: "20px",
                                    children: [
                                        /*#__PURE__*/ _jsx(GridItem, {
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                label: 'Cancel',
                                                variant: ButtonVariant.SECONDARY,
                                                onClick: handleCancelClick
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(GridItem, {
                                            children: /*#__PURE__*/ _jsx(Button, {
                                                label: `Apply`,
                                                dataTestId: `${label}-apply`.toLowerCase(),
                                                disabled: isOptionsChanged(),
                                                onClick: handleApplyClick
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
CheckboxMenu.displayName = 'CheckboxMenu';
export { CheckboxMenu };
